import React, { useEffect, useState, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import './QueuePage.scss'
import queueService from './services/QueueService';
import Section from '../../components/layout/Section';
import TicketQueueHeader from './components/TicketQueueHeader';
import QueueTable from './components/QueueTable';
import QueueSummary from './components/QueueSummary';
import { QueueTableTypes } from './enums/QueueTableTypes';
import ClearAllFilters from './components/ClearAllFilters';
import ErrorContent from '../../components/error/ErrorContent';
import BackLink from '../../components/library/common/BackLink';
import { FilterWorkflowMappingKeywords } from './enums/SortAndFilterTypes';
import HrbTranslate from '../../components/language/HrbTranslate';
import { useTimer } from '../../hooks/useTimer';
import Breadcrumb from '../../components/common/Breadcrumb/Breadcrumb';

const QueuePage = () => {
    var defaultFilterState = {
        reviewDocuments: false,
        prepareReturn: false,
        preparePayment: false,
        clientAction: false,
        fileReturn: false,
        hasUnreadMessage: false,
        closedTicketsOnly: false,
        skip: 0,
        orderBy: "clientLastActivity",
        order: "desc",
        associateSkip: null
    }
    const REFRESH_SPEED = 2 * 60;
    const history = useHistory();
    const { associateId } = useParams('associateId');
    const [queue, setQueue] = useState(null);
    const [shouldRefresh, setShouldRefresh] = useState(null);
    const [summary, setSummary] = useState(null);
    const [isMyQueue, setIsMyQueue] = useState(true);
    const [associateFullName, setAssociateFullName] = useState(null);
    const [associateFirstName, setAssociateFirstName] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const TICKET_FILTER = "ticket_search_criteria";
    const [filterState, setFilterState] = useState(JSON.parse(localStorage.getItem(TICKET_FILTER)) || defaultFilterState);
    const [, stop, isRunning] = useTimer(() => setShouldRefresh(true), REFRESH_SPEED, true);
    const location = useLocation();
    const { officeId, parentOfficeId } = location?.state ?? {}; 
    const loggedInUserId = useSelector(state => (state.accountData.employeeId || []));

    useEffect(() => {
        const handlePopState = (event) => {
            if (event.state.filterState || event.state.filterState !== undefined) {
                setFilterState(event.state.filterState);
            } else {
                setFilterState(defaultFilterState);
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
        window.removeEventListener('popstate', handlePopState);
        };
    }, []);
    
    const getSummary = async () => {
        var summaryData = await queueService.getQueueSummary(associateId)
        if (summaryData) {
            if (summaryData.error_code) {
                // handle the error
                setErrorCode(summaryData.error_code)
            } else {
                if (summaryData.associate_name && summaryData.associate_name !== associateFullName) {
                    setAssociateFullName(summaryData.associate_name)
                }
                if (summaryData.associate_first_name && summaryData.associate_first_name !== associateFirstName) {
                    setAssociateFirstName(summaryData.associate_first_name)
                }
                setSummary(summaryData);
            }
        }
    }

    const toggleFilter = (property) => {        
        const updatedFilterState = {
            ...filterState,
            [property]: !filterState[property],
        };
        setFilterState(updatedFilterState);
    };

    const updateSkip = (value) => {
        const updatedFilterState = {
            ...filterState,
            skip: value,
            associateSkip: {
                ...filterState.associateSkip,
                [associateId]: value,
            },
        };
        if(associateId === undefined) {
            updatedFilterState.associateSkip[loggedInUserId] = value;
        }
        
        window.history.pushState({ filterState: updatedFilterState }, null, window.location.pathname);
        setFilterState(updatedFilterState);

        var el = document ? document.getElementById("overflow-container") : null;
        if (el) {
            el.scrollTop = 0;
        }
    };

    const updateOrderBy = (orderByValue, orderValue) => {
        const updatedFilterState = {
            ...filterState,
            orderBy: orderByValue,
            order: orderValue,
        };
        setFilterState(updatedFilterState);
    };

    const createODataQuery = () => {
        const queryParts = [];
        const propertiesToIgnore = ['skip', 'orderBy', 'hasUnreadMessage', 'closedTicketsOnly', 'order'];

        if (filterState.hasUnreadMessage) {
            queryParts.push('hasUnreadMessage eq true');
        }

        if (filterState.closedTicketsOnly) {
            queryParts.push('ticketStateId gt 1');
        }
        else {
            queryParts.push('ticketStateId eq 1');
        }

        const orConditions = [];

        for (const property in filterState) {
            if (filterState[property] && !propertiesToIgnore.includes(property)) {
                if (property in FilterWorkflowMappingKeywords) {
                    const keywords = FilterWorkflowMappingKeywords[property];
                    const multipleKeywordQuery = keywords.map(keyword => `startswith(workflowStateIdentifier, '${keyword}')`).join(' or ');
                    orConditions.push(multipleKeywordQuery);
                }
            }
        }
        if (orConditions.length > 0) { queryParts.push(`(${orConditions.join(' or ')})`); }

        const skip = (filterState.associateSkip && filterState.associateSkip[associateId] !== undefined && filterState.associateSkip[associateId] !== null) ? filterState.associateSkip[associateId] : 0;
        const orderBy = filterState['orderBy'];
        const order = filterState['order'];

        if (queryParts.length > 0) {
            return `$filter=${queryParts.join(' and ')}&$skip=${skip}&$orderBy=${orderBy} ${order}`;
        } else {
            return `$skip=${filterState['skip']}&$orderBy=${filterState['orderBy']} ${order}`;
        }
    }

    const getQueue = async () => {
        var odataQuery = createODataQuery();
        var queueData = await queueService.getTicketsForAssociate(associateId, odataQuery)
        // Map OData response to JSON object required by child components
        queueData['items'] = queueData.value;
        queueData['totalCount'] = queueData['@odata.count'];
        queueData['limit'] = 25;
        queueData['offset'] = (filterState.associateSkip
            && filterState.associateSkip[associateId] !== undefined
            && filterState.associateSkip[associateId] !== null) ?
            filterState.associateSkip[associateId] : 0;
        // End
        if (queueData && queueData.error_code) {
            setErrorCode(queueData.error_code);
        } else {
            setQueue(queueData);
            localStorage.setItem('queueItemsCount', JSON.stringify(queueData?.items?.length))
        }
    }

    const getData = async () => {
        await Promise.all([getQueue(), getSummary()])
            .catch(err => {
                console.log(err)
            })
    }

    const employeeId = useSelector(state => state.accountData.employeeId)

    useEffect(() => {
        var isMe = !associateId || (`${associateId}` === `${employeeId}`);
        if (isMe !== isMyQueue) {
            setIsMyQueue(isMe);
            getData()
        }
    }, [employeeId, associateId])

    useEffect(() => {
        getData();
        const updatedFilterState = {
            ...filterState,
            skip: (filterState.associateSkip && filterState.associateSkip[associateId] !== undefined && filterState.associateSkip[associateId] !== null) ? filterState.associateSkip[associateId] : 0
        }
        localStorage.setItem(TICKET_FILTER, JSON.stringify(updatedFilterState))
    }, [filterState])

    useEffect(() => {
        if ((shouldRefresh && isRunning)) {
            getData();
        }
        setShouldRefresh(false);
    }, [shouldRefresh])

    useEffect(() => {
        return () => {
            if (isRunning) {
                stop()
            }
        }
    }, []);

    const handleViewTarget = (targetId) => {
        history.push(`/clients/${targetId}`)
    }

    const handleClearWorkflowFilters = () => {
        const updatedFilterState = {
            ...filterState,
            reviewDocuments: false,
            prepareReturn: false,
            preparePayment: false,
            clientAction: false,
            fileReturn: false
        };
        setFilterState(updatedFilterState);
    }

    const handleClearTableFilters = () => {
        const updatedFilterState = {
            ...filterState,
            hasUnreadMessage: false,
            closedTicketsOnly: false
        };
        setFilterState(updatedFilterState);
    }

    return (
        <div className="content-grid queue" >
            <Section sectionId='section-queue-summary' className='white'>
                {!errorCode &&
                    <Fragment>
                        <div className='columns '>
                            {isMyQueue &&
                                <div className='column is-one-sixth is-offset-1' />}
                            {!isMyQueue &&
                                <div className='ml-5'>
                                    <Breadcrumb
                                        paths={[
                                            { href: '/', label: <HrbTranslate text='Breadcrumbs.HomePage' /> },
                                            { href: `/offices/${officeId || parentOfficeId}`, label: <HrbTranslate text='Breadcrumbs.OfficesPage' params={{ OfficeId: officeId || parentOfficeId }} /> },
                                            { href: `/queue/${associateId}`, label: <HrbTranslate text='Breadcrumbs.AssociateTicketsPage' params={{ AssociateFirstName: associateFirstName }} /> }
                                        ]}
                                    />
                                </div>}
                            <div className='column is-one-sixth is-offset-6 queue-clear-all-filters'>
                                <ClearAllFilters onClick={handleClearWorkflowFilters} textKey='QueuePage.Labels.ClearTicketFilters'/>
                            </div>
                        </div>
                        <div className='columns ticket-filter-header'>
                            <div className='column is-one-sixth is-offset-1'><HrbTranslate text={'QueuePage.Labels.WorkflowFilterHeader'} /></div>
                        </div>
                        <QueueSummary summary={summary} toggleFilter={toggleFilter} filterState={filterState} />
                    </Fragment>
                }
                {errorCode === 400 &&
                    <ErrorContent errorDetailKey='QueuePage.Errors.NoSuchUserDetail' errorTitleKey='QueuePage.Errors.NoSuchUserTitle' />
                }
                {errorCode === 403 &&
                    <ErrorContent errorDetailKey='Errors.NotAllowedDetail' errorTitleKey='Errors.NotAllowedTitle' />
                }
            </Section>
            {!errorCode &&
                <Section sectionId='section-queue' className='light-functional-grey'>
                    <TicketQueueHeader isAssociateQueue={!isMyQueue} associateFullName={associateFullName} />
                    <QueueTable queue={queue} filterState={filterState} handleClearTableFilters={handleClearTableFilters}
                        onOpenTarget={handleViewTarget} updateOrderBy={updateOrderBy} officeId={officeId || parentOfficeId} associateFirstName={associateFirstName}
                        onSetOffset={updateSkip} tableType={QueueTableTypes.DEFAULT} isUnassignedQueue={false} associateId={associateId}
                        isAssociateQueue={!isMyQueue} associateFullName={associateFullName} toggleFilter={toggleFilter} />
                </Section>
            }
        </div>
    );
}

QueuePage.whyDidYouRender = false;

export default QueuePage